import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { BFF_BASE_URL } from '../../auth/authApi';

export interface Candidate {
  id: string;
  user_id: string;
  category: string;
  key: string;
  value: string;
  confidence: number;
  status: 'verified' | 'candidate';
  verified: boolean;
  updated_at: string;
  source: string;
  display_name?: string;
}

export interface ProfileState {
  userId: string | null;
  name: string;
  email: string;
  preferences: Record<string, unknown>;
  candidates: Candidate[];
  loading: boolean;
  error: string | null;
}

export const profileInitialState: ProfileState = {
  userId: null,
  name: '',
  email: '',
  preferences: {},
  candidates: [],
  loading: false,
  error: null,
};

export const fetchCandidates = createAsyncThunk(
  'profile/fetchCandidates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BFF_BASE_URL}/admin/candidates`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.error || error.message);
      }
      return rejectWithValue('Failed to fetch candidates');
    }
  }
);

const profileSlice = createSlice({
  name: 'profile',
  initialState: profileInitialState,
  reducers: {
    setProfile: (state, action: PayloadAction<Partial<ProfileState>>) => {
      Object.assign(state, action.payload);
      state.loading = false;
      state.error = null;
    },
    setCandidates: (state, action: PayloadAction<Candidate[]>) => {
      state.candidates = action.payload;
    },
    addCandidate: (state, action: PayloadAction<Candidate>) => {
      state.candidates.push(action.payload);
    },
    updateCandidate: (state, action: PayloadAction<Candidate>) => {
      const index = state.candidates.findIndex(c => c.id === action.payload.id);
      if (index !== -1) {
        state.candidates[index] = action.payload;
      }
    },
    deleteCandidate: (state, action: PayloadAction<string>) => {
      state.candidates = state.candidates.filter(c => c.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCandidates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.candidates = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchCandidates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setProfile,
  setCandidates,
  addCandidate,
  updateCandidate,
  deleteCandidate,
  setLoading,
  setError,
} = profileSlice.actions;

export default profileSlice;
